import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Primitives";
import { useSearchParams } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function TeacherResults() {
  const [tests, setTests] = useState([]);
  const [overview, setOverview] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [sp] = useSearchParams();

  const mcqTests = tests.filter((t) => t.type === "mcq");
  const selectedTestMeta = mcqTests.find((t) => t.id === selectedTest);

  useEffect(() => {
    api.get("/tests").then((r) => {
      setTests(r.data);
      const pre = sp.get("test");
      if (pre) setSelectedTest(pre);
    });
  }, [sp]);

  useEffect(() => {
    setLoadingSummary(true);
    if (!selectedTest) {
      setSummary(null);
      setLoadingSummary(false);
      return;
    }
    api
      .get(`/analytics/class/${selectedTest}`)
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoadingSummary(false));
  }, [selectedTest]);

  useEffect(() => {
    setLoadingOverview(true);
    const params = selectedTest ? { test_id: selectedTest } : {};
    api
      .get("/analytics/students-overview", { params })
      .then((r) => setOverview(r.data))
      .catch(() => setOverview([]))
      .finally(() => setLoadingOverview(false));
  }, [selectedTest]);

  const standingsTitle = selectedTestMeta
    ? `Standings for "${selectedTestMeta.title}"`
    : "Overall student standings (all tests)";

  const scoreColumnLabel = selectedTest ? "Score (this test)" : "Tests taken";
  const overallColumnLabel = selectedTest ? "Percentage" : "Overall";

  return (
    <div>
      <PageHeader
        eyebrow="Analytics"
        title="Results & performance"
        subtitle="Per-test summary, class averages and overall student standing."
      />

      <div className="card-academic p-6 mb-6">
        <label className="overline block mb-2">Select test</label>
        <select
          value={selectedTest || ""}
          onChange={(e) => setSelectedTest(e.target.value || null)}
          data-testid="test-select"
          className="w-full bg-ivory border border-stone px-4 py-3"
        >
          <option value="">All tests (overview)</option>
          {mcqTests.map((t) => (
            <option key={t.id} value={t.id}>
              {t.title}
            </option>
          ))}
        </select>
        <p className="mt-2 text-xs text-mossy">
          {selectedTest
            ? "Table and chart below show results for the selected test only."
            : "Choose a test to filter the class sheet and standings, or leave on overview for all tests combined."}
        </p>
      </div>

      <Section title="Class summary sheet">
        {!selectedTest ? (
          <EmptyState title="Select a test" body="Pick a test above to view the class summary sheet." />
        ) : loadingSummary ? (
          <div className="text-sm text-mossy p-6">Loading summary…</div>
        ) : summary ? (
          <div className="card-academic overflow-hidden">
            <div className="p-6 bg-sand border-b border-stone flex items-center justify-between">
              <div>
                <div className="overline">{summary.test.class_group}</div>
                <h3 className="font-serif text-2xl text-forest">{summary.test.title}</h3>
              </div>
              <div className="text-right">
                <div className="overline">Class average</div>
                <div className="font-mono text-3xl font-semibold text-forest">
                  {summary.class_average}%
                </div>
              </div>
            </div>
            {summary.attempts.length === 0 ? (
              <div className="p-6 text-sm text-mossy">No attempts yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-sand/60">
                  <tr>
                    <th className="p-4 overline text-left">Rank</th>
                    <th className="p-4 overline text-left">Student</th>
                    <th className="p-4 overline text-left">Score</th>
                    <th className="p-4 overline text-right">Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.attempts.map((a, i) => (
                    <tr key={a.id} className="border-b border-stone">
                      <td className="p-4 font-mono">{String(i + 1).padStart(2, "0")}</td>
                      <td className="p-4">{a.student_name}</td>
                      <td className="p-4 font-mono">
                        {a.score} / {a.max_score}
                      </td>
                      <td className="p-4 text-right font-mono font-semibold text-forest">
                        {a.percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <EmptyState title="No data" body="Could not load summary for this test." />
        )}
      </Section>

      <Section title={standingsTitle}>
        {loadingOverview ? (
          <div className="text-sm text-mossy p-6">Loading standings…</div>
        ) : overview.length === 0 ? (
          <EmptyState title="No students" body="No students match this test's class and section." />
        ) : (
          <>
            <div className="card-academic p-6 mb-4" style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview}>
                  <CartesianGrid stroke="#D8D3C8" strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#526055" />
                  <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#526055" />
                  <Tooltip formatter={(value) => [`${value}%`, "Percentage"]} />
                  <Bar dataKey="overall_percentage" fill="#1B3C35" name="Percentage" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-academic overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-sand">
                  <tr>
                    <th className="p-4 overline text-left">Student</th>
                    <th className="p-4 overline text-left">Class</th>
                    <th className="p-4 overline text-left">{scoreColumnLabel}</th>
                    <th className="p-4 overline text-right">{overallColumnLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.map((o) => (
                    <tr key={o.id} className="border-b border-stone">
                      <td className="p-4">{o.name}</td>
                      <td className="p-4 text-mossy">{o.class_group || "—"}</td>
                      <td className="p-4 font-mono">
                        {selectedTest
                          ? o.tests_taken > 0
                            ? `${o.score} / ${o.max_score}`
                            : "Not attempted"
                          : o.tests_taken}
                      </td>
                      <td className="p-4 text-right font-mono font-semibold text-forest">
                        {o.overall_percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}
